// src/validation/index.ts
import { z } from "zod";
var emailSchema = z.string().email("Invalid email address").min(5, "Email must be at least 5 characters").max(255, "Email must be less than 255 characters");
var passwordSchema = z.string().min(8, "Password must be at least 8 characters").max(128, "Password must be less than 128 characters").regex(/[A-Z]/, "Password must contain at least one uppercase letter").regex(/[a-z]/, "Password must contain at least one lowercase letter").regex(/[0-9]/, "Password must contain at least one number");
var termsAcceptanceInputSchema = z.object({
  termsVersion: z.string().min(1, "Versi\xF3n de T\xE9rminos requerida"),
  privacyVersion: z.string().min(1, "Versi\xF3n de Privacidad requerida"),
  contextPath: z.string().max(255).optional()
});
var registerSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  firstName: z.string().min(1, "First name is required").max(50),
  lastName: z.string().min(1, "Last name is required").max(50),
  termsAcceptance: termsAcceptanceInputSchema.optional()
});
var loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, "Password is required")
});
var forgotPasswordSchema = z.object({
  email: emailSchema
});
var resetPasswordSchema = z.object({
  token: z.string().min(1, "Token is required"),
  password: passwordSchema
});
var currencyEnum = z.enum(["USD", "CRC"]);
var createCourseSchema = z.object({
  title: z.string().min(3, "Title must be at least 3 characters").max(200, "Title must be less than 200 characters"),
  description: z.string().min(10, "Description must be at least 10 characters").max(5e3, "Description must be less than 5000 characters"),
  shortDescription: z.string().max(300).optional(),
  thumbnailUrl: z.string().url("Invalid thumbnail URL").optional().or(z.literal("")),
  isFree: z.boolean().default(false),
  listPrice: z.number().min(0, "El precio de lista debe ser positivo").nullish(),
  salePrice: z.number().min(0, "El precio de venta debe ser positivo").nullish(),
  hasDiscount: z.boolean().default(false),
  currency: currencyEnum.default("USD"),
  level: z.enum(["BEGINNER", "INTERMEDIATE", "ADVANCED"]).optional().or(z.literal("")),
  // Duración en minutos. La interfaz la pide en horas académicas y multiplica
  // por 60; el certificado la divide entre 60 para imprimir las horas.
  duration: z.number().int("La duraci\xF3n debe ser un n\xFAmero entero de minutos").min(0, "La duraci\xF3n no puede ser negativa").max(6e4, "La duraci\xF3n no puede exceder 1000 horas").nullish(),
  categoryIds: z.array(z.string()).max(5).optional()
});
var updateCourseSchema = createCourseSchema.partial().extend({
  status: z.enum(["DRAFT", "PUBLISHED", "ARCHIVED"]).optional()
});
var createChapterSchema = z.object({
  courseId: z.string().min(1, "Course ID is required"),
  title: z.string().min(1, "Title is required").max(200),
  description: z.string().max(1e3).optional(),
  content: z.string().optional(),
  videoUrl: z.string().url("Invalid video URL").optional().or(z.literal("")),
  videoDuration: z.number().min(0).optional().nullable(),
  position: z.number().int().min(1).optional(),
  isFree: z.boolean().default(false),
  isPublished: z.boolean().default(false),
  requiresQuizPass: z.boolean().default(false),
  minQuizScore: z.number().min(0).max(100).default(70)
});
var updateChapterSchema = createChapterSchema.partial().omit({ courseId: true }).extend({
  isPublished: z.boolean().optional(),
  position: z.number().int().min(1).optional()
});
var createQuestionSchema = z.object({
  text: z.string().min(1, "Question text is required").max(1e3),
  type: z.enum(["SINGLE_CHOICE", "MULTIPLE_CHOICE", "TRUE_FALSE"]).default("SINGLE_CHOICE"),
  explanation: z.string().max(500).optional(),
  points: z.number().min(1).default(1),
  options: z.array(
    z.object({
      text: z.string().min(1, "Option text is required"),
      isCorrect: z.boolean()
    })
  ).min(2, "At least 2 options are required").max(6, "Maximum 6 options allowed").refine(
    (options) => options.some((opt) => opt.isCorrect),
    "At least one option must be correct"
  )
});
var createQuizSchema = z.object({
  chapterId: z.string().min(1, "Chapter ID is required"),
  title: z.string().min(1, "Title is required").max(200),
  description: z.string().max(500).optional(),
  passingScore: z.number().min(0).max(100).default(70),
  maxAttempts: z.number().min(1).optional(),
  timeLimit: z.number().min(1).optional(),
  // in minutes
  shuffleQuestions: z.boolean().default(false),
  questions: z.array(createQuestionSchema).min(1, "At least 1 question is required")
});
var quizSubmissionSchema = z.object({
  quizId: z.string().min(1),
  answers: z.array(
    z.object({
      questionId: z.string().min(1),
      selectedOptionId: z.string().min(1)
    })
  )
});
var submitQuizSchema = z.object({
  answers: z.array(
    z.object({
      questionId: z.string().min(1),
      selectedOptionIds: z.array(z.string().min(1))
    })
  )
});
var paginationSchema = z.object({
  page: z.coerce.number().min(1).default(1),
  limit: z.coerce.number().min(1).max(100).default(10)
});
var tempFileSchema = z.object({
  url: z.string().url(),
  publicId: z.string(),
  type: z.enum(["PDF", "IMAGE"]),
  name: z.string(),
  mimeType: z.string(),
  size: z.number(),
  extractedText: z.string().optional()
});
var wizardChapterSchema = z.object({
  title: z.string().min(1, "El t\xEDtulo es requerido"),
  description: z.string().optional(),
  estimatedDuration: z.number().min(1).default(15),
  includeQuiz: z.boolean().default(true)
});
var aiGenerationSchema = z.object({
  topic: z.string().min(5, "El tema debe tener al menos 5 caracteres").max(200, "El tema debe tener menos de 200 caracteres"),
  duration: z.enum(["SHORT", "MEDIUM", "LONG"], {
    errorMap: () => ({ message: "Selecciona una duraci\xF3n v\xE1lida" })
  }),
  level: z.enum(["BEGINNER", "INTERMEDIATE", "ADVANCED"], {
    errorMap: () => ({ message: "Selecciona un nivel v\xE1lido" })
  }),
  language: z.enum(["es", "en"]).default("es"),
  includeQuizzes: z.boolean().default(true),
  additionalNotes: z.string().max(2e3, "Las notas deben tener menos de 2000 caracteres").optional(),
  tempFiles: z.array(tempFileSchema).optional(),
  // Custom chapters from wizard (optional - if not provided, uses DURATION_CONFIG)
  chapters: z.array(wizardChapterSchema).optional()
});
function validateEmail(email) {
  return emailSchema.safeParse(email).success;
}
function validatePassword(password) {
  const result = passwordSchema.safeParse(password);
  if (result.success) {
    return { valid: true, errors: [] };
  }
  return {
    valid: false,
    errors: result.error.errors.map((e) => e.message)
  };
}
var createModuleSchema = z.object({
  courseId: z.string().min(1, "El ID del curso es requerido"),
  title: z.string().min(1, "El t\xEDtulo es requerido").max(200, "El t\xEDtulo debe tener menos de 200 caracteres"),
  description: z.string().max(1e3).optional(),
  position: z.number().int().min(0).optional()
});
var updateModuleSchema = createModuleSchema.omit({ courseId: true }).partial();
var reorderModulesSchema = z.object({
  moduleIds: z.array(z.string()).min(1, "Se requiere al menos un m\xF3dulo")
});
var assignChaptersToModuleSchema = z.object({
  moduleId: z.string().min(1, "El ID del m\xF3dulo es requerido"),
  chapterIds: z.array(z.string())
});
var finalExamSectionEnum = z.enum([
  "CONCEPTUAL",
  "APPLICATION",
  "INTEGRATIVE_CASE"
]);
var createFinalExamQuestionSchema = z.object({
  text: z.string().min(1, "El texto de la pregunta es requerido").max(2e3, "El texto debe tener menos de 2000 caracteres"),
  explanation: z.string().max(1e3).optional(),
  points: z.number().min(1).default(1),
  section: finalExamSectionEnum,
  difficulty: z.enum(["EASY", "MEDIUM", "HARD"]).default("MEDIUM"),
  tags: z.array(z.string()).max(10).default([]),
  options: z.array(
    z.object({
      text: z.string().min(1, "El texto de la opci\xF3n es requerido"),
      isCorrect: z.boolean()
    })
  ).min(2, "Se requieren al menos 2 opciones").max(6, "M\xE1ximo 6 opciones permitidas").refine(
    (options) => options.some((opt) => opt.isCorrect),
    "Al menos una opci\xF3n debe ser correcta"
  )
});
var createFinalExamSchema = z.object({
  courseId: z.string().min(1, "El ID del curso es requerido"),
  title: z.string().min(1, "El t\xEDtulo es requerido").max(200, "El t\xEDtulo debe tener menos de 200 caracteres"),
  description: z.string().max(1e3).optional(),
  passingScore: z.number().min(0).max(100).default(70),
  maxAttempts: z.number().min(1).max(5).default(2),
  timeLimit: z.number().min(1).optional().nullable(),
  // en minutos
  shuffleQuestions: z.boolean().default(false),
  shuffleOptions: z.boolean().default(false),
  requiredForCertificate: z.boolean().default(true),
  // Pesos por sección (deben sumar 100)
  conceptualWeight: z.number().min(20).max(30).default(25),
  applicationWeight: z.number().min(30).max(40).default(35),
  integrativeCaseWeight: z.number().min(30).max(40).default(40),
  // Preguntas por sección por intento (null = usar todas)
  conceptualQuestionsPerAttempt: z.number().min(1).optional().nullable(),
  applicationQuestionsPerAttempt: z.number().min(1).optional().nullable(),
  integrativeCaseQuestionsPerAttempt: z.number().min(1).optional().nullable(),
  questions: z.array(createFinalExamQuestionSchema).min(3, "Se requieren al menos 3 preguntas (una por secci\xF3n)")
}).refine(
  (data) => {
    const total = data.conceptualWeight + data.applicationWeight + data.integrativeCaseWeight;
    return total === 100;
  },
  { message: "Los pesos de las secciones deben sumar 100%" }
).refine(
  (data) => {
    const sections = {
      CONCEPTUAL: 0,
      APPLICATION: 0,
      INTEGRATIVE_CASE: 0
    };
    for (const q of data.questions) {
      sections[q.section]++;
    }
    return sections.CONCEPTUAL > 0 && sections.APPLICATION > 0 && sections.INTEGRATIVE_CASE > 0;
  },
  { message: "Cada secci\xF3n debe tener al menos una pregunta" }
);
var updateFinalExamSchema = z.object({
  title: z.string().min(1).max(200).optional(),
  description: z.string().max(1e3).optional().nullable(),
  passingScore: z.number().min(0).max(100).optional(),
  maxAttempts: z.number().min(1).max(5).optional(),
  timeLimit: z.number().min(1).optional().nullable(),
  shuffleQuestions: z.boolean().optional(),
  shuffleOptions: z.boolean().optional(),
  requiredForCertificate: z.boolean().optional(),
  // Pesos por sección
  conceptualWeight: z.number().min(20).max(30).optional(),
  applicationWeight: z.number().min(30).max(40).optional(),
  integrativeCaseWeight: z.number().min(30).max(40).optional(),
  // Pooling por sección
  conceptualQuestionsPerAttempt: z.number().min(1).optional().nullable(),
  applicationQuestionsPerAttempt: z.number().min(1).optional().nullable(),
  integrativeCaseQuestionsPerAttempt: z.number().min(1).optional().nullable()
}).refine(
  (data) => {
    if (data.conceptualWeight !== void 0 && data.applicationWeight !== void 0 && data.integrativeCaseWeight !== void 0) {
      return data.conceptualWeight + data.applicationWeight + data.integrativeCaseWeight === 100;
    }
    return true;
  },
  { message: "Si actualizas los pesos, deben sumar 100%" }
);
var submitFinalExamSchema = z.object({
  attemptId: z.string().min(1, "El ID del intento es requerido"),
  answers: z.array(
    z.object({
      questionId: z.string().min(1),
      selectedOptionId: z.string().min(1)
    })
  )
});
var employeeCountRangeEnum = z.enum([
  "SMALL",
  "MEDIUM",
  "LARGE",
  "ENTERPRISE"
]);
var contactRequestStatusEnum = z.enum([
  "PENDING",
  "CONTACTED",
  "CONVERTED",
  "CLOSED"
]);
var createEnterpriseContactSchema = z.object({
  companyName: z.string().min(1, "El nombre de la empresa es requerido").max(200, "El nombre debe tener menos de 200 caracteres"),
  contactName: z.string().min(1, "El nombre del contacto es requerido").max(100, "El nombre debe tener menos de 100 caracteres"),
  email: emailSchema,
  phone: z.string().max(20).optional().or(z.literal("")),
  // El formulario corto de la web no pregunta el tamaño de la empresa
  employeeCount: employeeCountRangeEnum.optional(),
  message: z.string().min(10, "El mensaje debe tener al menos 10 caracteres").max(2e3, "El mensaje debe tener menos de 2000 caracteres"),
  // Programa o capacitación desde la que se solicitó, si aplica
  offeringId: z.string().min(1).optional()
});
var updateContactRequestStatusSchema = z.object({
  status: contactRequestStatusEnum
});
var addContactNoteSchema = z.object({
  content: z.string().min(1, "El contenido es requerido").max(2e3, "El contenido debe tener menos de 2000 caracteres")
});
var teamMemberRoleEnum = z.enum([
  "ACADEMIC_DIRECTOR",
  "EXECUTIVE_DIRECTOR",
  "INSTRUCTOR",
  "OTHER"
]);
var createTeamMemberSchema = z.object({
  name: z.string().min(1, "El nombre es requerido").max(100, "El nombre debe tener menos de 100 caracteres"),
  role: teamMemberRoleEnum,
  title: z.string().min(1, "El t\xEDtulo es requerido").max(100, "El t\xEDtulo debe tener menos de 100 caracteres"),
  photoUrl: z.string().url("URL de foto inv\xE1lida").optional().or(z.literal("")),
  bio: z.string().min(10, "La biograf\xEDa debe tener al menos 10 caracteres").max(2e3, "La biograf\xEDa debe tener menos de 2000 caracteres"),
  linkedInUrl: z.string().url("URL de LinkedIn inv\xE1lida").optional().or(z.literal("")),
  signatureUrl: z.string().url("URL de firma inv\xE1lida").optional().or(z.literal("")),
  position: z.number().int().min(0).default(0),
  isActive: z.boolean().default(true)
});
var updateTeamMemberSchema = createTeamMemberSchema.partial();
var courseLevelEnum = z.enum([
  "BEGINNER",
  "INTERMEDIATE",
  "ADVANCED",
  "EXPERT"
]);
var COURSE_LEVEL_LABELS = {
  BEGINNER: "Principiante",
  INTERMEDIATE: "Intermedio",
  ADVANCED: "Avanzado",
  EXPERT: "Experto"
};
var updateCourseExtendedSchema = z.object({
  objective: z.string().max(2e3).optional().nullable(),
  targetAudience: z.string().max(2e3).optional().nullable(),
  expectedResults: z.array(z.string()).max(20).optional().nullable()
});
var materialCategoryEnum = z.enum([
  "LECTURA",
  "PRACTICA",
  "TRABAJO",
  "MANUAL",
  "GUIA",
  "RECURSO"
]);
var MATERIAL_CATEGORY_LABELS = {
  LECTURA: "Lectura",
  PRACTICA: "Pr\xE1ctica",
  TRABAJO: "Trabajo",
  MANUAL: "Manual",
  GUIA: "Gu\xEDa",
  RECURSO: "Recurso descargable"
};
var createMaterialSchema = z.object({
  title: z.string().min(3, "El t\xEDtulo debe tener al menos 3 caracteres").max(200, "El t\xEDtulo debe tener menos de 200 caracteres"),
  description: z.string().max(2e3).optional().nullable(),
  coverImageUrl: z.string().url("URL de portada inv\xE1lida").optional().or(z.literal("")),
  fileUrl: z.string().url("URL de archivo inv\xE1lida"),
  fileName: z.string().max(255).optional().nullable(),
  fileSizeBytes: z.number().int().min(0).optional().nullable(),
  fileType: z.string().max(20).optional().nullable(),
  category: materialCategoryEnum.default("RECURSO"),
  isFree: z.boolean().default(false),
  listPrice: z.number().min(0).optional().nullable(),
  salePrice: z.number().min(0).default(0),
  hasDiscount: z.boolean().default(false),
  currency: currencyEnum.default("USD"),
  isPublished: z.boolean().default(false),
  courseId: z.string().optional().nullable()
});
var updateMaterialSchema = createMaterialSchema.partial();
var updateSystemSettingSchema = z.object({
  key: z.string().min(1, "La clave es requerida"),
  value: z.string(),
  type: z.enum(["string", "json", "boolean", "number"]).default("string")
});
var offeringCategoryEnum = z.enum(["PROGRAMA", "CAPACITACION"]);
var createOfferingSchema = z.object({
  category: offeringCategoryEnum,
  title: z.string().min(3, "El t\xEDtulo debe tener al menos 3 caracteres").max(250, "El t\xEDtulo debe tener menos de 250 caracteres"),
  summary: z.string().min(20, "La descripci\xF3n debe tener al menos 20 caracteres").max(3e3, "La descripci\xF3n debe tener menos de 3000 caracteres"),
  // "Áreas clave" en programas, "Enfoque" en capacitaciones
  focusAreas: z.array(z.string().min(1).max(120)).min(1, "Agregue al menos un \xE1rea o enfoque").max(12, "No agregue m\xE1s de 12 \xE1reas"),
  // Texto libre: las duraciones reales son rangos ("16 a 24 horas")
  durationLabel: z.string().min(1, "La duraci\xF3n es requerida").max(60, "La duraci\xF3n debe tener menos de 60 caracteres"),
  modalities: z.array(z.string().min(1).max(60)).min(1, "Indique al menos una modalidad").max(6, "No agregue m\xE1s de 6 modalidades"),
  coverImageUrl: z.string().url("URL de imagen inv\xE1lida").nullish().or(z.literal("")),
  isPublished: z.boolean().default(false),
  position: z.number().int().min(0).optional()
});
var updateOfferingSchema = createOfferingSchema.partial();
var reorderOfferingsSchema = z.object({
  offerings: z.array(
    z.object({
      id: z.string().min(1),
      position: z.number().int().min(0)
    })
  ).min(1, "Env\xEDe al menos un elemento")
});

// src/formatting/index.ts
function formatDate(date, options) {
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    ...options
  });
}
function formatDateTime(date) {
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
}
function formatRelativeTime(date) {
  const d = typeof date === "string" ? new Date(date) : date;
  const now = /* @__PURE__ */ new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffSecs = Math.floor(diffMs / 1e3);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffSecs < 60) return "just now";
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? "s" : ""} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
  return formatDate(d);
}
function formatDuration(minutes) {
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (remainingMinutes === 0) return `${hours}h`;
  return `${hours}h ${remainingMinutes}m`;
}
function formatDurationLong(minutes) {
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? "s" : ""}`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  const hoursStr = `${hours} hour${hours !== 1 ? "s" : ""}`;
  if (remainingMinutes === 0) return hoursStr;
  return `${hoursStr} ${remainingMinutes} minute${remainingMinutes !== 1 ? "s" : ""}`;
}
function formatCurrency(amount, currency = "USD") {
  if (currency === "CRC") {
    return `\u20A1${new Intl.NumberFormat("es-CR", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)}`;
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency
  }).format(amount);
}
function formatNumber(num) {
  return new Intl.NumberFormat("en-US").format(num);
}
function formatCompact(num) {
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    compactDisplay: "short"
  }).format(num);
}
function formatPercentage(value, decimals = 0) {
  return `${value.toFixed(decimals)}%`;
}
function slugify(text) {
  return text.toLowerCase().trim().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^\w\s-]/g, "").replace(/[\s_-]+/g, "-").replace(/^-+|-+$/g, "");
}
function truncate(text, maxLength) {
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 3)}...`;
}
function formatFileSize(bytes) {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}
function extractYouTubeId(url) {
  const regex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
  const match = url.match(regex);
  return match ? match[1] ?? null : null;
}
function extractVimeoId(url) {
  const regex = /(?:vimeo\.com\/)(\d+)/;
  const match = url.match(regex);
  return match ? match[1] ?? null : null;
}
function getVideoProvider(url) {
  if (url.includes("youtube.com") || url.includes("youtu.be")) return "youtube";
  if (url.includes("vimeo.com")) return "vimeo";
  return null;
}

// src/helpers/index.ts
async function tryCatch(promise) {
  try {
    const data = await promise;
    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error : new Error(String(error))
    };
  }
}
function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
function generateId() {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 11)}`;
}
function deepClone(obj) {
  return JSON.parse(JSON.stringify(obj));
}
function omit(obj, keys) {
  const result = { ...obj };
  keys.forEach((key) => delete result[key]);
  return result;
}
function pick(obj, keys) {
  const result = {};
  keys.forEach((key) => {
    if (key in obj) result[key] = obj[key];
  });
  return result;
}
function groupBy(array, keyFn) {
  return array.reduce(
    (result, item) => {
      const key = keyFn(item);
      (result[key] = result[key] || []).push(item);
      return result;
    },
    {}
  );
}
function unique(array) {
  return [...new Set(array)];
}
function chunk(array, size) {
  const chunks = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
}
function shuffle(array) {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}
function debounce(fn, waitMs) {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), waitMs);
  };
}
function throttle(fn, limitMs) {
  let lastCall = 0;
  return (...args) => {
    const now = Date.now();
    if (now - lastCall >= limitMs) {
      lastCall = now;
      fn(...args);
    }
  };
}
async function retry(fn, options = {}) {
  const { attempts = 3, delay: delayMs = 1e3, backoff = true } = options;
  let lastError;
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      if (i < attempts - 1) {
        const waitTime = backoff ? delayMs * Math.pow(2, i) : delayMs;
        await delay(waitTime);
      }
    }
  }
  throw lastError;
}
function isServer() {
  return typeof globalThis.window === "undefined";
}
function isClient() {
  return typeof globalThis.window !== "undefined";
}
function safeJsonParse(json, fallback) {
  try {
    return JSON.parse(json);
  } catch {
    return fallback;
  }
}
function capitalize(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}
function randomColor() {
  return `#${Math.floor(Math.random() * 16777215).toString(16).padStart(6, "0")}`;
}
export {
  COURSE_LEVEL_LABELS,
  MATERIAL_CATEGORY_LABELS,
  addContactNoteSchema,
  aiGenerationSchema,
  assignChaptersToModuleSchema,
  capitalize,
  chunk,
  contactRequestStatusEnum,
  courseLevelEnum,
  createChapterSchema,
  createCourseSchema,
  createEnterpriseContactSchema,
  createFinalExamQuestionSchema,
  createFinalExamSchema,
  createMaterialSchema,
  createModuleSchema,
  createOfferingSchema,
  createQuestionSchema,
  createQuizSchema,
  createTeamMemberSchema,
  currencyEnum,
  debounce,
  deepClone,
  delay,
  emailSchema,
  employeeCountRangeEnum,
  extractVimeoId,
  extractYouTubeId,
  finalExamSectionEnum,
  forgotPasswordSchema,
  formatCompact,
  formatCurrency,
  formatDate,
  formatDateTime,
  formatDuration,
  formatDurationLong,
  formatFileSize,
  formatNumber,
  formatPercentage,
  formatRelativeTime,
  generateId,
  getVideoProvider,
  groupBy,
  isClient,
  isServer,
  loginSchema,
  materialCategoryEnum,
  offeringCategoryEnum,
  omit,
  paginationSchema,
  passwordSchema,
  pick,
  quizSubmissionSchema,
  randomColor,
  registerSchema,
  reorderModulesSchema,
  reorderOfferingsSchema,
  resetPasswordSchema,
  retry,
  safeJsonParse,
  shuffle,
  slugify,
  submitFinalExamSchema,
  submitQuizSchema,
  teamMemberRoleEnum,
  tempFileSchema,
  termsAcceptanceInputSchema,
  throttle,
  truncate,
  tryCatch,
  unique,
  updateChapterSchema,
  updateContactRequestStatusSchema,
  updateCourseExtendedSchema,
  updateCourseSchema,
  updateFinalExamSchema,
  updateMaterialSchema,
  updateModuleSchema,
  updateOfferingSchema,
  updateSystemSettingSchema,
  updateTeamMemberSchema,
  validateEmail,
  validatePassword
};
